export function escapeMarkdownCharactersIfNeeded(html: string) {
  const htmlTagRegex = /(<.+?>)/gm;
  let nestedCodeLevel = 0;
  let nestedPreLevel = 0;

  return html
    .split(htmlTagRegex)
    .map(part => {
      if (part.match(htmlTagRegex)) {
        if (part.match(/<code/)) nestedCodeLevel++;
        if (part.match(/<\/code/)) nestedCodeLevel--;
        if (part.match(/<pre/)) nestedPreLevel++;
        if (part.match(/<\/pre/)) nestedPreLevel--;
        return part;
      } else if (nestedCodeLevel > 0 || nestedPreLevel > 0) {
        return part;
      }
      return escapeMDText(part);
    })
    .join('');
}

export function fixNestedListsAndWhitespaceInEmphasisNodes(html: string) {
  const nbsp = '\u00A0';
  const nodeContainer = document.createElement('wrapper');
  nodeContainer.innerHTML = html.replace(/&nbsp;/gm, nbsp);

  const emphasisNodes = Array.from(
    nodeContainer.querySelectorAll('b, em, i, strike, strong')
  );
  const whitespaceNodes = emphasisNodes.filter(hasLeadingOrTrailingWhitespace);
  const newLineNodes = emphasisNodes.filter(hasOnlyNewLines);
  whitespaceNodes.forEach(moveWhitespaceOutsideOfNode);
  newLineNodes.forEach(replaceWithBr);
  // pre-processes list items to strict html standard to allow indentation
  const topSubLists = getTopSubLists(nodeContainer.querySelectorAll('ol, ul'));
  topSubLists.forEach(fixNestedLists);

  return nodeContainer.innerHTML.replace(/\u00A0/gm, '&nbsp;');
}

function escapeMDText(mdText: string) {
  const filters = [
    {
      label: 'escape backslash',
      matcher: /\\/gm,
      replacer: '\\\\',
    },
    {
      label:
        'escape markdown characters, minus dot, pound and backslash, + pipe: tables',
      matcher: /(?:\\)?(`|\*|_|{|}|\[|]|\(|\)|\+|!|~|\|)/gm,
      replacer: '\\$1',
    },
    {
      label: 'escape pound - take care not to escape #tags',
      matcher: /#(?!\w+)|^#(?=\w+)/gm,
      replacer: '\\#',
    },
    {
      label: 'orderedList',
      matcher: /^((?:&nbsp;|\s)*\d+)\.(&nbsp;|\s)+/gm,
      replacer: '$1\\.$2',
      match: 0,
      prefixWhitespace: 1,
      suffixWhitespace: 2,
    },
  ];

  return filters.reduce(
    (text, filter) => text.replace(filter.matcher, filter.replacer),
    mdText
  );
}

function hasLeadingOrTrailingWhitespace(node: Node) {
  return (
    node.textContent &&
    node.textContent.trim().length !== node.textContent.length
  );
}

function hasOnlyNewLines(node: Element) {
  const matcher = /<(b|em|i|strike|strong)><br><\/\1>/gm;
  return matcher.test(node.outerHTML);
}

const moveWhitespaceOutsideOfNode = (node: Element) => {
  const matcher = /^([\s\u00A0]*)(.+?)([\s\u00A0]*)$/gm;
  const result = matcher.exec(node.textContent || '');
  if (result) {
    const [, leadingWhitespace, trimmedContent, trailingWhitespace] = result;
    if (leadingWhitespace) {
      node.insertAdjacentText('beforebegin', leadingWhitespace);
    }
    node.innerHTML = trimmedContent;
    if (trailingWhitespace) {
      node.insertAdjacentText('afterend', trailingWhitespace);
    }
  }
};

function replaceWithBr(node: Element) {
  node.outerHTML = '<br>';
}

/*
 * used to find top sub lists for a given list
 */
function getTopSubLists(
  lists: NodeListOf<HTMLUListElement | HTMLOListElement>
) {
  const topLists = Array.from(lists);

  lists.forEach(outerElement => {
    lists.forEach(innerElement => {
      const listIndex = topLists.indexOf(innerElement);

      if (
        listIndex > -1 &&
        outerElement !== innerElement &&
        outerElement.contains(innerElement)
      ) {
        topLists.splice(listIndex, 1);
      }
    });
  });

  return topLists;
}

function fixNestedLists(element: HTMLUListElement | HTMLOListElement) {
  const listNodes = Array.from(element.querySelectorAll('ol, ul'));
  listNodes.forEach((listNode: any) => {
    const parent = listNode.parentNode;
    if (parent && parent.tagName === 'LI') {
      return;
    }
    const prev = listNode.previousElementSibling;
    if (prev && prev.matches('li')) {
      prev.appendChild(listNode);
    } else {
      const li = document.createElement('li');
      parent.insertBefore(li, listNode);
      li.appendChild(listNode);
    }
  });
}

export function getAlignment(alignment: string | null) {
  switch (alignment) {
    case 'center':
      return ':---:';
    case 'right':
      return '---:';
    case 'left':
      return ':---';
    default:
      return '---';
  }
}

// ARD-22383: Remove all breaks when next sibling is either an ordered or unordered list
export function fixBreaksBeforeLists(html: string) {
  const nodeContainer = document.createElement('wrapper');
  nodeContainer.innerHTML = html;
  const listsAfterBreaks = nodeContainer.querySelectorAll('br + ol, br + ul');

  for (const list of listsAfterBreaks) {
    list.previousElementSibling?.remove();
  }

  return nodeContainer.innerHTML;
}
